import {
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";

import { payWithTotp } from "@/api/pay_helpers";
import {
  steamHeaderUrl,
  useBuySteamGift,
  useSearchSteamGifts,
  useSteamGiftAppDetail,
  useSteamGiftService,
  type SteamGiftAppDetail,
  type SteamGiftSearchHit,
  type SteamGiftSub,
  type SteamGiftSubRegionPrice,
} from "@/api/steam_gifts";
import { useT } from "@/lib/i18n";
import { useToasts } from "@/store/toasts";

const REGION_LABELS: Record<string, string> = {
  ru: "RU",
  kz: "KZ",
  ua: "UA",
  cis: "CIS",
  cn: "CN",
};

const REGION_LONG: Record<string, string> = {
  ru: "Russia",
  kz: "Kazakhstan",
  ua: "Ukraine",
  cis: "CIS",
  cn: "China",
};

function regionShort(r: string): string {
  return REGION_LABELS[r] ?? r.toUpperCase();
}

function regionLong(r: string): string {
  return REGION_LONG[r] ?? r.toUpperCase();
}

function regionCurrency(r: string): string {
  switch (r) {
    case "ru":
      return "₽";
    case "kz":
      return "₸";
    case "ua":
      return "₴";
    case "cn":
      return "¥";
    case "cis":
      return "$";
    default:
      return "";
  }
}

export function SteamGifts() {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [openAppId, setOpenAppId] = useState<number | null>(null);

  // Debounce - fast typist shouldn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const service = useSteamGiftService();
  // Server returns [] for empty q; gating on the client too keeps the
  // network silent when the user hasn't typed anything.
  const search = useSearchSteamGifts(debouncedQuery, debouncedQuery.length > 0);

  const t = useT();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-800">{t("steam_gift.title")}</h1>
        <p className="mt-1 text-sm text-slate-500">{t("steam_gift.subtitle")}</p>
      </div>

      {service.isError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {t("steam_gift.unconfigured")}
        </div>
      )}

      <div className="card p-4">
        <input
          type="search"
          className="input"
          placeholder={t("steam_gift.search.placeholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
      </div>

      {/* Empty state - only shown when user hasn't typed yet. */}
      {!debouncedQuery && (
        <div className="rounded-md border border-dashed border-surface-border bg-white px-4 py-10 text-center text-sm text-slate-400">
          {t("steam_gift.search.start")}
        </div>
      )}

      {debouncedQuery && search.isLoading && (
        <div className="text-sm text-slate-400">{t("steam_gift.search.busy")}</div>
      )}
      {debouncedQuery && search.isError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {t("steam_gift.search.failed")}
        </div>
      )}
      {debouncedQuery &&
        search.data &&
        search.data.items.length === 0 && (
          <div className="text-sm text-slate-400">
            {t("steam_gift.search.empty").replace("{q}", debouncedQuery)}
          </div>
        )}

      {debouncedQuery && search.data && search.data.items.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {search.data.items.map((hit) => (
            <SearchHitCard
              key={hit.app_id}
              hit={hit}
              onSelect={() => setOpenAppId(hit.app_id)}
            />
          ))}
        </div>
      )}

      {openAppId !== null && service.data && (
        <BuyModal
          appId={openAppId}
          serviceId={service.data.service_id}
          onClose={() => setOpenAppId(null)}
        />
      )}
    </div>
  );
}

// ---- search hit card -----------------------------------------------------

function SearchHitCard({
  hit,
  onSelect,
}: {
  hit: SteamGiftSearchHit;
  onSelect: () => void;
}) {
  const [imgFailed, setImgFailed] = useState(false);
  const t = useT();

  return (
    <button
      onClick={onSelect}
      className="card group overflow-hidden text-left transition hover:border-accent"
    >
      <div className="relative aspect-[460/215] w-full bg-surface-muted">
        {!imgFailed ? (
          <img
            src={steamHeaderUrl(hit.app_id)}
            alt={hit.name}
            className="h-full w-full object-cover"
            loading="lazy"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-xs text-slate-400">
            {t("steam_gift.no_image")}
          </div>
        )}
      </div>
      <div className="space-y-0.5 p-3">
        <div className="text-[10px] uppercase tracking-wide text-slate-400">
          {t("steam_gift.app")} {hit.app_id}
        </div>
        <div className="line-clamp-2 text-sm font-medium text-slate-800 group-hover:text-accent">
          {hit.name}
        </div>
      </div>
    </button>
  );
}

// ---- modal ---------------------------------------------------------------

function BuyModal({
  appId,
  serviceId,
  onClose,
}: {
  appId: number;
  serviceId: number;
  onClose: () => void;
}) {
  const detail = useSteamGiftAppDetail(appId);
  const t = useT();
  const detailLoadingLabel = t("steam_gift.detail.loading");
  const appDetailFailLabel = t("explore.failed");

  // ESC + click-outside to close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/60 p-2 sm:p-4"
      onClick={onClose}
    >
      <div
        className="card my-2 w-full max-w-2xl overflow-hidden bg-white sm:my-8"
        onClick={(e) => e.stopPropagation()}
      >
        {detail.isLoading && (
          <div className="px-6 py-12 text-center text-sm text-slate-400">
            {detailLoadingLabel}
          </div>
        )}
        {detail.isError && (
          <div className="m-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {appDetailFailLabel}
          </div>
        )}
        {detail.data && (
          <BuyForm
            app={detail.data}
            appId={appId}
            serviceId={serviceId}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}

// ---- buy form ------------------------------------------------------------

function BuyForm({
  app,
  appId,
  serviceId,
  onClose,
}: {
  app: SteamGiftAppDetail;
  appId: number;
  serviceId: number;
  onClose: () => void;
}) {
  const push = useToasts((s) => s.push);
  const buy = useBuySteamGift();
  const t = useT();
  const [imgFailed, setImgFailed] = useState(false);

  const [selected, setSelected] = useState<{
    sub_id: number;
    region: string;
  } | null>(null);

  const [friendLink, setFriendLink] = useState("");
  const [giftName, setGiftName] = useState(app.name);
  const [giftDescription, setGiftDescription] = useState("");

  const selectedRegionRow = useMemo(() => {
    if (!selected) return null;
    const sub = app.subs.find((s) => s.sub_id === selected.sub_id);
    return sub?.regions.find((r) => r.region === selected.region) ?? null;
  }, [selected, app.subs]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!selected || !selectedRegionRow) {
      push({ kind: "error", title: "Pick a price first" });
      return;
    }
    const link = friendLink.trim();
    if (!/^https:\/\/s\.team\/p\/[A-Za-z0-9-]+\/[A-Za-z0-9]+$/.test(link)) {
      push({
        kind: "error",
        title: "Invalid friend link",
        body: "Expected a https://s.team/p/<token>/<userid> URL.",
      });
      return;
    }
    if (!giftName.trim()) {
      push({ kind: "error", title: "Gift title is required" });
      return;
    }

    try {
      const result = await payWithTotp(
        (code) =>
          buy.mutateAsync({
            service_id: serviceId,
            sub_id: selected.sub_id,
            region: selected.region,
            friend_link: link,
            gift_name: giftName.trim(),
            gift_description: giftDescription.trim(),
            ...(code ? { totp_code: code } : {}),
          }),
        { title: "Confirm gift purchase" },
      );
      if (result === null) return; // cancelled
      const isAsync = result.status === "in_progress";
      push({
        kind: isAsync ? "info" : "success",
        title: isAsync ? "Gift in progress" : "Gift sent",
        body: isAsync
          ? "Delivery is being processed. Watch the order page for the final status."
          : "Order completed.",
        link: {
          to: `/orders/${encodeURIComponent(result.custom_id)}`,
          label: "View order",
        },
      });
      onClose();
    } catch (err) {
      const message =
        err instanceof Error && "detail" in err
          ? String((err as { detail: unknown }).detail ?? err.message)
          : "Could not place gift order";
      push({ kind: "error", title: "Order failed", body: message });
    }
  };

  // header_image from Steam's appdetails (Django parity); fall back
  // to the canonical CDN convention if Steam timed out.
  const heroSrc =
    !imgFailed && app.header_image ? app.header_image : steamHeaderUrl(appId);

  if (app.subs.length === 0) {
    return (
      <>
        <ModalHero
          src={heroSrc}
          name={app.name}
          appId={appId}
          onClose={onClose}
          onError={() => setImgFailed(true)}
        />
        <div className="px-5 py-6 text-sm text-slate-500">
          {t("steam_gift.detail.no_subs")}
        </div>
      </>
    );
  }

  return (
    <form onSubmit={submit}>
      <ModalHero
        src={heroSrc}
        name={app.name}
        appId={appId}
        onClose={onClose}
        onError={() => setImgFailed(true)}
      />

      <div className="space-y-4 p-3 sm:p-5">
        {/* Subs + price grid as a tight table - Django parity. */}
        <div className="space-y-3">
          {app.subs.map((sub) => (
            <SubPriceTable
              key={sub.sub_id}
              sub={sub}
              selected={selected}
              onSelect={(region) =>
                setSelected({ sub_id: sub.sub_id, region })
              }
            />
          ))}
        </div>

        <div className="grid gap-3 border-t border-surface-border pt-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="label" htmlFor="friend_link">
              {t("steam_gift.friend_label")}
            </label>
            <input
              id="friend_link"
              type="url"
              className="input"
              placeholder={t("steam_gift.friend_placeholder")}
              value={friendLink}
              onChange={(e) => setFriendLink(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="label" htmlFor="gift_name">
              {t("steam_gift.gift_title_label")}
            </label>
            <input
              id="gift_name"
              type="text"
              className="input"
              value={giftName}
              onChange={(e) => setGiftName(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="label" htmlFor="gift_description">
              {t("steam_gift.gift_description_label")}
            </label>
            <input
              id="gift_description"
              type="text"
              className="input"
              placeholder={t("steam_gift.gift_description_placeholder")}
              value={giftDescription}
              onChange={(e) => setGiftDescription(e.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-surface-border pt-3 text-sm">
          <div>
            {selectedRegionRow ? (
              <>
                <span className="text-slate-500">{t("steam_gift.youll_pay")}</span>{" "}
                <span className="font-semibold text-slate-800">
                  ${selectedRegionRow.usd_cost.toFixed(4)}
                </span>{" "}
                <span className="text-xs text-slate-400">
                  ({regionLong(selectedRegionRow.region)})
                </span>
              </>
            ) : (
              <span className="text-slate-400">{t("steam_gift.pick_region")}</span>
            )}
          </div>
          <button
            type="submit"
            className="btn btn-primary text-sm"
            disabled={buy.isPending || !selected}
          >
            {buy.isPending ? t("steam_gift.cta_busy") : t("steam_gift.cta")}
          </button>
        </div>
      </div>
    </form>
  );
}

// ---- shared ----

function ModalHero({
  src,
  name,
  appId,
  onClose,
  onError,
}: {
  src: string;
  name: string;
  appId: number;
  onClose: () => void;
  onError: () => void;
}) {
  return (
    <div className="relative">
      <div className="aspect-[460/215] w-full bg-surface-muted">
        <img
          src={src}
          alt={name}
          className="h-full w-full object-cover"
          onError={onError}
        />
      </div>
      <button
        type="button"
        onClick={onClose}
        className="absolute right-2 top-2 rounded-full bg-slate-900/70 px-2.5 py-0.5 text-xs font-medium text-white hover:bg-slate-900"
      >
        ✕
      </button>
      <div className="border-b border-surface-border bg-white px-5 py-3">
        <div className="text-base font-semibold text-slate-800">{name}</div>
        <div className="text-[11px] uppercase tracking-wide text-slate-400">
          App {appId}
        </div>
      </div>
    </div>
  );
}

// ---- sub price table -----------------------------------------------------

function SubPriceTable({
  sub,
  selected,
  onSelect,
}: {
  sub: SteamGiftSub;
  selected: { sub_id: number; region: string } | null;
  onSelect: (region: string) => void;
}) {
  return (
    <div className="overflow-hidden rounded-md border border-surface-border">
      <div className="flex items-baseline justify-between gap-2 bg-surface-muted px-3 py-2 text-xs">
        <div className="truncate font-medium text-slate-700">
          {sub.name || `Package ${sub.sub_id}`}
        </div>
        <div className="text-slate-400">#{sub.sub_id}</div>
      </div>
      <table className="w-full text-xs">
        <thead className="text-slate-500">
          <tr className="text-left">
            <th className="px-3 py-1.5 font-normal">Region</th>
            <th className="px-3 py-1.5 text-right font-normal">Local</th>
            <th className="px-3 py-1.5 text-right font-normal">USD</th>
          </tr>
        </thead>
        <tbody>
          {sub.regions.map((row) => (
            <PriceRow
              key={row.region}
              row={row}
              isSelected={
                selected?.sub_id === sub.sub_id &&
                selected?.region === row.region
              }
              onClick={() => onSelect(row.region)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PriceRow({
  row,
  isSelected,
  onClick,
}: {
  row: SteamGiftSubRegionPrice;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <tr
      onClick={onClick}
      className={`cursor-pointer border-t border-surface-border transition ${
        isSelected
          ? "bg-accent-soft text-accent"
          : "hover:bg-surface-muted"
      }`}
    >
      <td className="px-3 py-1.5 font-medium">
        {regionShort(row.region)}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums">
        {row.region_price.toFixed(2)} {regionCurrency(row.region)}
      </td>
      <td className="px-3 py-1.5 text-right font-semibold tabular-nums">
        ${row.usd_cost.toFixed(4)}
      </td>
    </tr>
  );
}
